import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppVersion from '../../components/AppVersion';
import '../Login/Login.css';

const MIN_PASSWORD_LENGTH = 8;
const CODE_LENGTH = 6;

// Passos do fluxo: informar e-mail -> conferir código de 6 dígitos -> definir nova senha
const STEP_EMAIL = 'email';
const STEP_CODE = 'code';
const STEP_PASSWORD = 'password';

// Passo anterior de cada passo, usado pela seta de "voltar" (ver handleBack) —
// no primeiro passo, a seta sai do fluxo e volta para o login.
const PREVIOUS_STEP = {
  [STEP_CODE]: STEP_EMAIL,
  [STEP_PASSWORD]: STEP_CODE,
};

function ForgotPassword() {
  const navigate = useNavigate();
  const { t: tLogin } = useTranslation('login');

  const [step, setStep] = useState(STEP_EMAIL);

  const [email, setEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [code, setCode] = useState('');
  const [actionToken, setActionToken] = useState('');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const requestCode = async (targetEmail) => {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/post_request_password_reset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: targetEmail }),
    });

    const data = await response.json();

    if (!response.ok || !data.status) {
      throw new Error(data.error || 'request_reset_failed');
    }

    return data.reset_token;
  };

  const handleRequestCode = async (event) => {
    event.preventDefault();
    setError('');
    setInfoMessage('');

    if (!email) {
      setError(tLogin('fillEmail'));
      return;
    }

    setIsSubmitting(true);
    try {
      const token = await requestCode(email);
      setResetToken(token);
      setStep(STEP_CODE);
    } catch (err) {
      console.error('Erro ao solicitar código de redefinição:', err);
      setError(err.message || tLogin('requestResetError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setInfoMessage('');
    setIsSubmitting(true);
    try {
      const token = await requestCode(email);
      setResetToken(token);
      setCode('');
      setInfoMessage(tLogin('codeResent'));
    } catch (err) {
      console.error('Erro ao reenviar código de redefinição:', err);
      setError(err.message || tLogin('requestResetError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyCode = async (event) => {
    event.preventDefault();
    setError('');
    setInfoMessage('');

    if (code.length !== CODE_LENGTH) {
      setError(tLogin('invalidCode'));
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/post_verify_reset_code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reset_token: resetToken, code }),
      });

      const data = await response.json();

      if (response.ok && data.status) {
        setActionToken(data.action_token);
        setStep(STEP_PASSWORD);
      } else {
        setError(tLogin('invalidCode'));
      }
    } catch (err) {
      console.error('Erro ao verificar código de redefinição:', err);
      setError(tLogin('requestResetError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async (event) => {
    event.preventDefault();
    setError('');

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(tLogin('passwordTooShort'));
      return;
    }

    if (password !== confirmPassword) {
      setError(tLogin('passwordsDontMatch'));
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/post_reset_password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action_token: actionToken, password }),
      });

      const data = await response.json();

      if (response.ok && data.status) {
        navigate('/', { replace: true });
      } else {
        setError(tLogin('passwordResetError'));
      }
    } catch (err) {
      console.error('Erro ao redefinir senha:', err);
      setError(tLogin('passwordResetError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    const previousStep = PREVIOUS_STEP[step];
    if (previousStep) {
      setError('');
      setInfoMessage('');
      setStep(previousStep);
    } else {
      navigate('/');
    }
  };

  const renderSideA = () => (
    <div className="image-carousel">
      <img src="/imagens/linhas.svg" className="background-svg" alt="Background Lines" />
      <img src="/imagens/logo-e-titulo-tpf.svg" className="logo-svg" alt="Logo TPF" />
    </div>
  );

  const renderEmailStep = () => (
    <form className="forms" onSubmit={handleRequestCode}>

      <div className="input-container-login">
        <input
          type="email"
          placeholder={tLogin('emailAddress')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="input-field"
          autoComplete="email"
          data-testid="input-forgot-email"
        />
        <img src="/imagens/e-mail_icon.svg" alt="" className="input-icon" />
      </div>

      {error && <p className="error-message">{error}</p>}

      <div>
        <button type="submit" className="primary-button" disabled={isSubmitting}>
          {isSubmitting ? tLogin('submitting') : tLogin('sendCodeButton')}
        </button>
      </div>
    </form>
  );

  const renderCodeStep = () => (
    <form className="forms" onSubmit={handleVerifyCode}>

      <div className="input-container-login">
        <input
          type="text"
          inputMode="numeric"
          maxLength={CODE_LENGTH}
          placeholder={tLogin('codePlaceholder')}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          className="input-field"
          data-testid="input-reset-code"
        />
        <img src="/imagens/lock.svg" alt="" className="input-icon" />
      </div>

      {infoMessage && !error && <p className="forgot-info-message">{infoMessage}</p>}
      {error && <p className="error-message">{error}</p>}

      <div>
        <button type="submit" className="primary-button" disabled={isSubmitting}>
          {isSubmitting ? tLogin('submitting') : tLogin('verifyCodeButton')}
        </button>
      </div>

      <button
        type="button"
        className="forgot-link-button"
        onClick={handleResendCode}
        disabled={isSubmitting}
      >
        {tLogin('resendCode')}
      </button>
    </form>
  );

  const renderPasswordStep = () => (
    <form className="forms" onSubmit={handleResetPassword}>
      <div className="input-container-login">
        <input
          type={showPassword ? 'text' : 'password'}
          placeholder={tLogin('newPassword')}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="input-field"
          autoComplete="new-password"
          data-testid="input-new-password"
        />
        <img src="/imagens/lock.svg" alt="" className="input-icon" />
        <img
          src={showPassword ? '/imagens/eye-open.svg' : '/imagens/eye-closed.svg'}
          alt=""
          className="toggle-password-icon"
          onClick={() => setShowPassword(!showPassword)}
        />
      </div>

      <div className="input-container-login">
        <input
          type={showConfirmPassword ? 'text' : 'password'}
          placeholder={tLogin('confirmPassword')}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          className="input-field"
          autoComplete="new-password"
          data-testid="input-confirm-password"
        />
        <img src="/imagens/lock.svg" alt="" className="input-icon" />
        <img
          src={showConfirmPassword ? '/imagens/eye-open.svg' : '/imagens/eye-closed.svg'}
          alt=""
          className="toggle-password-icon"
          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
        />
      </div>

      {!error && <p className="forgot-info-message">{tLogin('passwordRequirements')}</p>}

      {error && <p className="error-message">{error}</p>}

      <div>
        <button type="submit" className="primary-button" disabled={isSubmitting}>
          {isSubmitting ? tLogin('submitting') : tLogin('changePasswordButton')}
        </button>
      </div>
    </form>
  );

  const titleByStep = {
    [STEP_EMAIL]: tLogin('changeYourPassword'),
    [STEP_CODE]: tLogin('codeStepTitle'),
    [STEP_PASSWORD]: tLogin('changePasswordTitle'),
  };

  return (
    <div className="login-container">
      {renderSideA()}

      <div className="sideb-container show">
        <div className="form-container">
          <div className="forgot-centered-container">

            <img
              src="imagens\left_icon.svg"
              className="forgot-back-icon"
              alt="Icone para voltar"
              onClick={handleBack}
            />

            <div className="welcome-container">
              <h3 className="welcome-title">{titleByStep[step]}</h3>
            </div>

            {step === STEP_EMAIL && renderEmailStep()}
            {step === STEP_CODE && renderCodeStep()}
            {step === STEP_PASSWORD && renderPasswordStep()}
          </div>
        </div>

        <footer className="footer-login"></footer>
      </div>

      <AppVersion />
    </div>
  );
}

export default ForgotPassword;
